import {useMemo} from "react";
import {useInstructorTrainings} from "../firebase/useInstructorTrainings";
import {usePublishedInstructors} from "../firebase/usePublishedInstructors";
import {isCourseActive} from "../instructors/courseValidation";

export const useUnifiedCourses = ({
    searchTerm = "",
    selectedCounty = "Alle",
    selectedFormat = "Alle",
} = {}) => {
    const {trainings, loading: loadingTrainings, refresh: refreshTrainings, saveTraining, deleteTraining} =
        useInstructorTrainings();
    const rawInstructors = usePublishedInstructors({});

    // 1. Flatten all active local courses from certified instructors
    const citizenCourses = useMemo(() => {
        if (!Array.isArray(rawInstructors)) return [];
        const result = [];

        rawInstructors.forEach((instructor) => {
            if (Array.isArray(instructor.upcomingCourses)) {
                instructor.upcomingCourses.forEach((course) => {
                    if (isCourseActive(course)) {
                        result.push({
                            ...course,
                            instructorId: instructor.id,
                            instructorName: instructor.name,
                            instructorTitle: instructor.title || "",
                            instructorPlace: instructor.place || "",
                            city: instructor.city || instructor.place || "",
                            county: instructor.county || "",
                            instructorPhone: instructor.phone || "",
                            instructorEmail: instructor.email || "",
                            instructorPhotoUrl: instructor.photoUrl || instructor.image || "",
                            courseFormat: course.isDigital ? "digital" : "fysisk",
                        });
                    }
                });
            }
        });

        // Sort by start date ascending
        return result.sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
    }, [rawInstructors]);

    // 2. Official instructor trainings (for professionals)
    const professionalTrainings = useMemo(() => {
        if (!Array.isArray(trainings)) return [];
        return trainings
            .filter((t) => t.published !== false)
            .sort((a, b) => (Number(a.order) || 99) - (Number(b.order) || 99));
    }, [trainings]);

    // 3. Filter citizen courses according to user selection
    const filteredCitizenCourses = useMemo(() => {
        return citizenCourses.filter((course) => {
            if (selectedCounty !== "Alle" && course.county !== selectedCounty) {
                return false;
            }
            if (selectedFormat !== "Alle" && course.courseFormat !== selectedFormat) {
                return false;
            }
            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase();
                const titleMatch = (course.title || "").toLowerCase().includes(term);
                const cityMatch = (course.city || "").toLowerCase().includes(term);
                const countyMatch = (course.county || "").toLowerCase().includes(term);
                const instructorMatch = (course.instructorName || "").toLowerCase().includes(term);
                return titleMatch || cityMatch || countyMatch || instructorMatch;
            }
            return true;
        });
    }, [citizenCourses, selectedCounty, selectedFormat, searchTerm]);

    // 4. Filter professional trainings
    const filteredProfessionalTrainings = useMemo(() => {
        return professionalTrainings.filter((training) => {
            if (selectedFormat !== "Alle" && training.format !== selectedFormat) {
                return false;
            }
            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase();
                const titleMatch = (training.title || "").toLowerCase().includes(term);
                const subMatch = (training.subtitle || "").toLowerCase().includes(term);
                const instructorMatch = (training.instructorName || "").toLowerCase().includes(term);
                return titleMatch || subMatch || instructorMatch;
            }
            return true;
        });
    }, [professionalTrainings, selectedFormat, searchTerm]);

    return {
        professionalTrainings,
        filteredProfessionalTrainings,
        citizenCourses,
        filteredCitizenCourses,
        loadingTrainings,
        refreshTrainings,
        saveTraining,
        deleteTraining,
    };
};
